import { ThemeProvider } from '@mui/material';
import { PostHogProvider } from 'posthog-js/react';
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';

import { ConditionalWrap } from '../components/ConditionalWrap.js';
import { Head } from '../components/Head.js';
import { config } from '../config/loader.js';
import { theme } from '../theme/theme.js';
import { Auth0ProviderConfigured } from './Auth0ProviderConfigured.js';
import GraphQLProvider from './GraphQLProvider.js';
import LanguageProvider from './LanguageProvider.js';

interface Props {
  children: React.ReactNode;
}

// Read the parent page's distinct_id from the URL *synchronously at module load*,
// before PostHogProvider mounts. Passing it via bootstrap.distinctID stops
// posthog-js from generating its own anonymous id, so early events (autocaptured
// $pageview, modal_step_1_completed) are attributed to the forwarded id instead
// of being stranded on a throwaway id until App.tsx's identify() effect runs.
function readBootstrapDistinctId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const urlParams = new URLSearchParams(window.location.search);
  const rawDistinctId = urlParams.get('posthog_distinct_id');
  const bootstrapDistinctId = rawDistinctId ? rawDistinctId : undefined;

  if (bootstrapDistinctId) {
    // Strip from URL so the distinct_id doesn't leak via shared/bookmarked links.
    urlParams.delete('posthog_distinct_id');
    const newSearch = urlParams.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', newUrl);
  }

  return bootstrapDistinctId;
}

const bootstrapDistinctId = readBootstrapDistinctId();

export default function CombinedProviders({ children }: Props): React.ReactElement {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ConditionalWrap
        condition={config.postHogEnabled}
        wrapper={(children) => (
          <PostHogProvider
            apiKey={config.postHogKey}
            options={{
              api_host: config.postHogHost,
              bootstrap: bootstrapDistinctId ? { distinctID: bootstrapDistinctId } : undefined,
            }}
          >
            {children}
          </PostHogProvider>
        )}
      >
        <Auth0ProviderConfigured>
          <GraphQLProvider>
            <ThemeProvider theme={theme}>
              <Head />
              <LanguageProvider>{children}</LanguageProvider>
            </ThemeProvider>
          </GraphQLProvider>
        </Auth0ProviderConfigured>
      </ConditionalWrap>
    </BrowserRouter>
    </HelmetProvider>
  );
}
